import { useCallback, useMemo, useRef, useState } from 'react';

import { useFulfillmentPlannerContext } from '../context';
import { getInsuranceProviderById } from '../../enums/insurance-providers';
import {
  APPLIED_INSURANCE_RATE_RECOMMENDATION,
  SHOWED_INSURANCE_RATE_RECOMMENDATION,
  APPLY_FROM_CSW,
  SHOW_FROM_CSW,
} from '../../constants/analytics-events';
import { Segment } from '../../services/analytics-services/segment';
import { v4 } from 'uuid';
import { redactPIIFromShippingConfiguration } from '../../services/redacted-information-service';
import { NumberFormattingService } from '../../services/number-formatting-service';
import { useLegacyInsuranceRecommendation } from './use-legacy-insurance-recommendation';
import { useAlternativeRatesInsuranceRecommendation } from './use-alternative-rates-insurance-recommendation';
import { trackShowRecommendation, trackApplyRecommendation } from './rate-recommendations.api';
import { RATE_BROWSER, CUSTOMERS_CREATE_LABEL } from '../../constants/fulfillment-plan-ids';

export const useInsuranceRateRecommendation = (fulfillmentPlanId, setShouldShowComparison, alternativeRatesRecommendation) => {
  const [insuranceProviderId, setInsuranceProviderId] = useState('');
  const [insuranceRateDifference, setInsuranceRateDifference] = useState({});
  const trackingData = useRef({});

  const { onInsuranceProviderChange } = useFulfillmentPlannerContext();

  const applyInsuranceRecommendation = useCallback(() => {
    if (trackingData.current) {
      const { analyticsData, sellerId, recommendation, originalShippingConfiguration, userRatesTotalCost } = trackingData.current;

      const { name } = getInsuranceProviderById(insuranceProviderId);
      const sendAnalytics = () => {
        trackApplyRecommendation({
          ...recommendation,
          fromServiceId: recommendation.toServiceId,
          fromSellerLabelProviderId: recommendation.toSellerLabelProviderId,
          fromCarrierId: recommendation.toCarrierId,
          fromInsuranceProvider: originalShippingConfiguration.insuranceProvider,
          fromTotalCost: userRatesTotalCost,
          fromShippingConfigurationHash: originalShippingConfiguration.shippingConfigurationHash,
        });
      };
      onInsuranceProviderChange(name, sendAnalytics);

      Segment.trackEvent(
        APPLIED_INSURANCE_RATE_RECOMMENDATION,
        {
          ...analyticsData,
          action: APPLY_FROM_CSW,
        },
        { sellerId },
      );
    }
  }, [insuranceProviderId, onInsuranceProviderChange]);

  const compareRates = useCallback(
    ({ userRatesTotalCost, comparedRatesTotalCost, comparedInsuranceProviderId, metadata }) => {
      const userTotal = userRatesTotalCost?.value;
      const comparedTotal = comparedRatesTotalCost?.value;

      if (userTotal > comparedTotal) {
        const rateDiffValue = NumberFormattingService.roundToPlace(userTotal - comparedTotal, 2);
        const { name } = getInsuranceProviderById(comparedInsuranceProviderId);
        setInsuranceRateDifference({
          value: rateDiffValue,
          code: userRatesTotalCost.code,
        });
        setInsuranceProviderId(comparedInsuranceProviderId);
        setShouldShowComparison(true);

        const { sellerId, originalShippingConfiguration } = metadata;

        const analyticsData = {
          action: SHOW_FROM_CSW,
          eventId: v4(),
          sellerId,
          fulfillmentPlanId,
          amountCheaperValue: rateDiffValue,
          amountCheaperCode: userRatesTotalCost.code,
          originalShippingConfiguration: redactPIIFromShippingConfiguration(originalShippingConfiguration),
          suggestedInsuranceProviderId: comparedInsuranceProviderId,
        };
        Segment.trackEvent(SHOWED_INSURANCE_RATE_RECOMMENDATION, analyticsData, { sellerId });

        const recommendation = {
          fulfillmentPlanId,
          toServiceId: originalShippingConfiguration.serviceId,
          toSellerLabelProviderId: originalShippingConfiguration.sellerLabelProviderId,
          toCarrierId: originalShippingConfiguration.carrierId,
          toInsuranceProvider: name,
          recommendationType: 'Insurance',
          toTotalCost: comparedRatesTotalCost,
          actionLocationType: 'CSW',
        };
        if (fulfillmentPlanId !== RATE_BROWSER && fulfillmentPlanId !== CUSTOMERS_CREATE_LABEL) {
          trackShowRecommendation(recommendation);
        }
        trackingData.current = {
          analyticsData,
          sellerId,
          recommendation,
          originalShippingConfiguration,
          userRatesTotalCost,
        };
      }
    },
    [fulfillmentPlanId, setShouldShowComparison],
  );

  useAlternativeRatesInsuranceRecommendation({
    fulfillmentPlanId,
    compareRates,
    setShouldShowComparison,
    alternativeRatesRecommendation,
  });

  useLegacyInsuranceRecommendation({
    fulfillmentPlanId,
    compareRates,
    setShouldShowComparison,
  });

  return useMemo(
    () => ({
      insuranceRateDifference,
      applyInsuranceRecommendation,
      insuranceProviderId,
    }),
    [insuranceRateDifference, applyInsuranceRecommendation, insuranceProviderId],
  );
};
